/**
 * Porter Art Guild — Custom Order Configurator
 * pac-configurator.js v3
 * Namespace: PorterConfigurator / pac-
 */

(function() {
  'use strict';

  // "Custom Order" placeholder product variant. The Cart Transform function
  // overrides this line's price from the _config_price property at checkout.
  const PAC_CUSTOM_VARIANT_ID = 53067831410912;

  const state = {
    artwork:   null,
    media:     null,
    width:     null,
    height:    null,
    price:     null,
    shipping:  null,
    crop:      { x: 0, y: 0 }, // normalized 0-1 offset of artwork within frame
    border:    true,           // paper/loose 1" white border (customer toggle, on by default)
    matSize:   2,              // framed mat width per side, inches: 0 (none) / 1 / 2 (default)
    matColor:  'White',        // framed mat colour: 'White' / 'Black' (no price effect; only when mat > 0)
  };

  const $ = id => document.getElementById(id);

  // ── Ratio helpers ────────────────────────────────────────────────────────
  function normalizeRatio(aspect) {
    if (!aspect) return null;
    return String(aspect).replace('/', ':');
  }

  function getRatioDecimal(ratioStr) {
    if (!ratioStr) return null;
    const parts = ratioStr.split(':');
    return parseFloat(parts[0]) / parseFloat(parts[1]);
  }

  function ratioFromDims(w, h) {
    if (!w || !h) return null;
    function gcd(a, b) { return b ? gcd(b, a % b) : a; }
    const d = gcd(Math.round(w), Math.round(h));
    return `${Math.round(w)/d}:${Math.round(h)/d}`;
  }

  function ratiosMatch(r1, r2) {
    if (!r1 || !r2) return false;
    const d1 = getRatioDecimal(r1);
    const d2 = getRatioDecimal(r2);
    return Math.abs(d1 - d2) < 0.02;
  }

  // ── Variant parser ───────────────────────────────────────────────────────
  function parseVariants(variantsObj) {
    if (!variantsObj || typeof variantsObj !== 'object') return [];
    return Object.entries(variantsObj).map(([key, data]) => {
      const parts   = key.split('-');
      const size    = parts[0];
      const typeKey = parts.slice(1).join('-');
      const typeMap = {
        'paper':   'Paper Print',
        'loose':   'Loose Canvas Print',
        'wrapped': 'Wrapped Canvas',
        'black':   'Framed Print - Black',
        'white':   'Framed Print - White',
        'natural': 'Framed Print - Natural',
      };
      return {
        id:           data.v,
        size,
        product_type: typeMap[typeKey] || typeKey,
        price:        parseFloat(data.p) || 0,
        available:    true,
        image:        data.i || '',
      };
    });
  }

  // ── Stage helpers ────────────────────────────────────────────────────────
  function unlockStage(id) { const el = $(id); if (el) el.classList.remove('pac-stage--locked'); }
  function lockStage(id)   { const el = $(id); if (el) el.classList.add('pac-stage--locked'); }
  function showStage(id)   { const el = $(id); if (el) el.classList.remove('pac-stage--hidden'); }
  function hideStage(id)   { const el = $(id); if (el) el.classList.add('pac-stage--hidden'); }

  // ── Catalog loader ───────────────────────────────────────────────────────
  async function loadCatalog() {
    if (window._pacCatalog) return window._pacCatalog;
    if (window._pacCatalogPromise) return window._pacCatalogPromise;

    window._pacCatalogPromise = (async () => {
      const maxPages = 20; // safety ceiling

      function parsePage(text) {
        const start = text.indexOf('"products":[');
        if (start === -1) return { products: [], pages: 0 };
        const arrStart = text.indexOf('[', start + 11);
        const arrEnd   = text.lastIndexOf(']') + 1;
        let products = [];
        try { products = JSON.parse(text.slice(arrStart, arrEnd)) || []; } catch (e) { products = []; }
        const pagesMatch = text.match(/"pages":(\d+)/);
        return { products, pages: pagesMatch ? parseInt(pagesMatch[1], 10) : 0 };
      }

      async function fetchPage(p) {
        try {
          const res = await fetch(`/pages/bundle-catalog?page=${p}`);
          return parsePage(await res.text());
        } catch (e) {
          return { products: [], pages: 0 };
        }
      }

      const first = await fetchPage(1);
      let products = first.products.slice();

      if (products.length) {
        const total = Math.min(first.pages || 15, maxPages);
        if (total > 1) {
          const rest = await Promise.all(
            Array.from({ length: total - 1 }, (_, i) => fetchPage(i + 2))
          );
          rest.forEach(r => { products = products.concat(r.products); });
        }
      }

      window._pacCatalog = products;
      return products;
    })();

    return window._pacCatalogPromise;
  }

  // ── Search ───────────────────────────────────────────────────────────────
  let searchTimer = null;

  function initSearch() {
    const input   = $('pac-search-input');
    const results = $('pac-search-results');
    if (!input) return;

    input.addEventListener('input', function() {
      clearTimeout(searchTimer);
      const q = this.value.trim();
      if (q.length < 2) {
        results.classList.remove('pac-search-results--open');
        return;
      }
      searchTimer = setTimeout(() => doSearch(q), 300);
    });

    document.addEventListener('click', function(e) {
      if (!e.target.closest('#pac-artwork-search')) {
        results.classList.remove('pac-search-results--open');
      }
    });
  }

  async function doSearch(q) {
    const results = $('pac-search-results');
    results.innerHTML = '<div class="pac-search-result"><span class="pac-search-result__title">Searching…</span></div>';
    results.classList.add('pac-search-results--open');

    try {
      const catalog = await loadCatalog();
      // Normalize query — remove punctuation, lowercase
      const query   = q.toLowerCase().replace(/[''']/g, '');

      const matches = catalog.filter(p => {
        const title = (p.title || '').toLowerCase().replace(/[''']/g, '');
        const tags  = Array.isArray(p.tags) ? p.tags.join(' ').toLowerCase() : '';
        return title.includes(query) || tags.includes(query);
      });

      if (!matches.length) {
        results.innerHTML = '<div class="pac-search-result"><span class="pac-search-result__title">No results found</span></div>';
        return;
      }

      results.innerHTML = matches.map(p => `
        <div class="pac-search-result" data-handle="${p.id}">
          <img src="${p.artwork_url || ''}" alt="${p.title}" onerror="this.style.display='none'">
          <div>
            <div class="pac-search-result__title">${p.title}</div>
            <div class="pac-search-result__ratio">${normalizeRatio(p.aspect) || ''}</div>
          </div>
        </div>
      `).join('');

      results.querySelectorAll('.pac-search-result').forEach(el => {
        el.addEventListener('click', () => selectArtwork(el.dataset.handle, catalog));
      });

    } catch(e) {
      console.error('PAC search error:', e);
      results.innerHTML = '<div class="pac-search-result"><span class="pac-search-result__title">Search unavailable</span></div>';
    }
  }

  async function loadAllVariants(handle) {
    // The bundle feed omits Paper/Loose (they're not bundle-eligible). Pull the full
    // variant list straight from the product so standard matches work for every media.
    try {
      const res = await fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } });
      if (!res.ok) return null;
      const data   = await res.json();
      const sizeRe = /\d+(?:\.\d+)?\s*[x×X]\s*\d+(?:\.\d+)?/;
      return (data.variants || []).map(function (v) {
        const opts = v.options || [];
        let size = '', media = '';
        opts.forEach(function (o) { if (sizeRe.test(String(o))) size = o; else media = o; });
        return { id: v.id, size: size, product_type: media };
      });
    } catch (e) { return null; }
  }

  function selectArtwork(handle, catalog) {
    const product = catalog.find(p => p.id === handle);
    if (!product) return;

    const variants = parseVariants(product.variants);

    state.artwork = {
      handle:      product.id,
      title:       product.title,
      image:       product.artwork_url || '',
      artwork_url: product.artwork_url || '',
      variants,
      allVariants: null,
      ratio:       normalizeRatio(product.aspect),
      sizes:       product.sizes || [],
      url:         `/products/${product.id}`,
    };

    const h = product.id;
    state.artwork.variantsPromise = loadAllVariants(h).then(function (vs) {
      if (state.artwork && state.artwork.handle === h && vs && vs.length) state.artwork.allVariants = vs;
      return vs;
    });

    onArtworkSelected();
  }

  function onArtworkSelected() {
    const art = state.artwork;
    if (!art) return;

    // Thumbnail — natural ratio via object-fit contain
    const thumb = $('pac-artwork-thumb');
    thumb.src   = art.artwork_url;
    thumb.style.objectFit = 'contain';

    $('pac-artwork-title').textContent      = art.title;
    $('pac-artwork-selected').style.display = 'flex';
    $('pac-artwork-search').style.display   = 'none';
    $('pac-search-results').classList.remove('pac-search-results--open');

    unlockStage('pac-stage-media');

    state.media  = null;
    state.width  = null;
    state.height = null;
    state.crop   = { x: 0, y: 0 };

    lockStage('pac-stage-size');
    clearPricing();
    clearMockup();

    document.querySelectorAll('#pac-media-pills .pac-pill').forEach(p => p.classList.remove('pac-pill--active'));

    if (art.ratio) {
      $('pac-ratio-note').textContent = `Original ratio: ${art.ratio}`;
    }
    updateBorderToggleVisibility();
  }

  function initChangeArtwork() {
    const btn = $('pac-change-artwork');
    if (!btn) return;
    btn.addEventListener('click', function() {
      state.artwork = null;
      $('pac-artwork-selected').style.display = 'none';
      $('pac-artwork-search').style.display   = 'block';
      $('pac-search-input').value = '';
      $('pac-search-input').focus();
      lockStage('pac-stage-media');
      lockStage('pac-stage-size');
      clearPricing();
      clearMockup();
      hideCropTool();
      var bt = $('pac-border-toggle');
      if (bt) bt.style.display = 'none';
      var mc = $('pac-mat-controls');
      if (mc) mc.style.display = 'none';
    });
  }

  // ── Media ────────────────────────────────────────────────────────────────
  function initMediaPills() {
    document.querySelectorAll('#pac-media-pills .pac-pill').forEach(pill => {
      pill.addEventListener('click', function() {
        document.querySelectorAll('#pac-media-pills .pac-pill').forEach(p => p.classList.remove('pac-pill--active'));
        this.classList.add('pac-pill--active');
        state.media = this.dataset.media;
        onMediaSelected();
      });
    });
  }

  async function onMediaSelected() {
    if (!state.artwork || !state.media) return;

    unlockStage('pac-stage-size');
    populateQuickSizes();

    state.width  = null;
    state.height = null;
    state.crop   = { x: 0, y: 0 };
    $('pac-width').value  = '';
    $('pac-height').value = '';
    $('pac-size-note').textContent = '';
    clearPricing();
    clearMockup();
    hideCropTool();
    state.matSize  = 2;
    state.matColor = 'White';
    updateBorderToggleVisibility();
    updateMatUIVisibility();
  }

  // ── Quick sizes ──────────────────────────────────────────────────────────
  function populateQuickSizes() {
    const container = $('pac-quick-sizes');
    if (!container || !state.artwork) return;
    container.innerHTML = '';

    (state.artwork.sizes || []).forEach(sz => {
      const btn       = document.createElement('button');
      btn.className   = 'pac-pill';
      btn.textContent = sz;
      btn.dataset.size = sz;
      btn.addEventListener('click', function() {
        document.querySelectorAll('#pac-quick-sizes .pac-pill').forEach(p => p.classList.remove('pac-pill--active'));
        this.classList.add('pac-pill--active');
        const m = sz.match(/(\d+\.?\d*)[x×](\d+\.?\d*)/i);
        if (m) {
          state.width  = parseFloat(m[1]);
          state.height = parseFloat(m[2]);
          $('pac-width').value  = state.width;
          $('pac-height').value = state.height;
          $('pac-size-note').textContent = '';
          state.crop = { x: 0, y: 0 };
          onSizeChanged();
        }
      });
      container.appendChild(btn);
    });
  }

  // ── Size inputs ──────────────────────────────────────────────────────────
  function initSizeInputs() {
    ['pac-width', 'pac-height'].forEach(id => {
      const el = $(id);
      if (!el) return;
      el.addEventListener('input', function() {
        document.querySelectorAll('#pac-quick-sizes .pac-pill').forEach(p => p.classList.remove('pac-pill--active'));
        state.width  = parseFloat($('pac-width').value)  || null;
        state.height = parseFloat($('pac-height').value) || null;
        state.crop   = { x: 0, y: 0 };
        if (state.width && state.height) {
          onSizeChanged();
        } else {
          clearPricing();
          hideCropTool();
        }
      });
    });
  }

  async function onSizeChanged() {
    if (!state.width || !state.height || !state.media || !state.artwork) return;

    const Data = window.PorterConfigurator && window.PorterConfigurator.Data;
    if (!Data) return;

    $('pac-size-note').textContent = '';

    // Size limit check
    try {
      const check = await Data.checkSizeLimits(state.media, state.width, state.height, pacMatIn());
      if (!check.valid) {
        $('pac-size-note').textContent = check.errors[0];
        clearPricing();
        clearMockup();
        return;
      }
    } catch(e) {}

    // Pricing
    try {
      const ratio          = state.artwork.ratio;
      if (!ratio) return;
      let   priceResult    = await Data.getPrice(ratio, state.media, state.width, state.height);
      if (pacIsFramed(state.media)) {
        const mf = pacMatFactor();
        const floor10 = function (n) { return Math.floor(n * 10) / 10; };
        priceResult = Object.assign({}, priceResult, {
          basePrice:  priceResult.basePrice  * mf,
          totalPrice: floor10(priceResult.totalPrice * mf),
        });
        if (typeof priceResult.surcharge === 'number') priceResult.surcharge = priceResult.surcharge * mf;
      }
      const overall        = pacOverall();
      const bIn            = pacBorderIn(state.media);
      const shipW          = pacIsFramed(state.media) ? overall[0] : state.width  + 2 * bIn; // framed ships at outer matted+framed size
      const shipH          = pacIsFramed(state.media) ? overall[1] : state.height + 2 * bIn;
      const shippingResult = await Data.getShipping(state.media, shipW, shipH, priceResult.totalPrice);

      state.price    = priceResult;
      state.shipping = shippingResult;

      updatePricingDisplay();
      updateMockup();
      checkVariantMatch();

    } catch(e) {
      console.error('PAC: pricing error', e);
    }
  }

  // ── Crop tool ────────────────────────────────────────────────────────────
  function showCropTool() {
    let cropWrap = $('pac-crop-wrap');
    if (!cropWrap) {
      cropWrap = document.createElement('div');
      cropWrap.id        = 'pac-crop-wrap';
      cropWrap.className = 'pac-crop-wrap';
      cropWrap.innerHTML = `
        <p class="pac-stage__label" style="margin-bottom:8px;">CROP — Drag to reposition</p>
        <div class="pac-crop-viewport" id="pac-crop-viewport">
          <img id="pac-crop-img" class="pac-crop-img" draggable="false">
          <div class="pac-crop-overlay" id="pac-crop-overlay"></div>
        </div>
        <p class="pac-crop-hint">Drag the image to choose which part will be printed</p>
      `;
      // Insert after size stage
      const sizeStage = $('pac-stage-size');
      sizeStage.parentNode.insertBefore(cropWrap, sizeStage.nextSibling);
      initCropDrag();
    }

    // Set image
    const img = $('pac-crop-img');
    img.src   = state.artwork.artwork_url;

    // Set viewport aspect ratio to match requested output size
    const viewport   = $('pac-crop-viewport');
    const vpW        = viewport.clientWidth || 300;
    const vpH        = (vpW / state.width) * state.height;
    viewport.style.height = `${Math.min(vpH, 300)}px`;

    // Position image to fill viewport maintaining its natural ratio
    updateCropPosition();
    cropWrap.style.display = 'block';
  }

  function hideCropTool() {
    const cropWrap = $('pac-crop-wrap');
    if (cropWrap) cropWrap.style.display = 'none';
  }

  function updateCropPosition() {
    const img      = $('pac-crop-img');
    const viewport = $('pac-crop-viewport');
    if (!img || !viewport || !state.artwork) return;

    const artRatio  = getRatioDecimal(state.artwork.ratio);
    const outRatio  = state.width / state.height;
    const vpW       = viewport.clientWidth;
    const vpH       = viewport.clientHeight;

    let imgW, imgH;
    if (artRatio > outRatio) {
      // artwork wider than output — constrain height, overflow width
      imgH = vpH;
      imgW = vpH * artRatio;
    } else {
      // artwork taller than output — constrain width, overflow height
      imgW = vpW;
      imgH = vpW / artRatio;
    }

    const maxX = Math.max(0, imgW - vpW);
    const maxY = Math.max(0, imgH - vpH);
    const offX  = -state.crop.x * maxX;
    const offY  = -state.crop.y * maxY;

    img.style.width     = `${imgW}px`;
    img.style.height    = `${imgH}px`;
    img.style.transform = `translate(${offX}px, ${offY}px)`;
  }

  function initCropDrag() {
    const viewport = $('pac-crop-viewport');
    if (!viewport) return;

    let dragging = false;
    let startX, startY, startCropX, startCropY;

    function getArtRatio() { return getRatioDecimal(state.artwork ? state.artwork.ratio : null); }

    function onDown(e) {
      dragging  = true;
      const pt  = e.touches ? e.touches[0] : e;
      startX    = pt.clientX;
      startY    = pt.clientY;
      startCropX = state.crop.x;
      startCropY = state.crop.y;
      e.preventDefault();
    }

    function onMove(e) {
      if (!dragging) return;
      const pt    = e.touches ? e.touches[0] : e;
      const dx    = pt.clientX - startX;
      const dy    = pt.clientY - startY;
      const img   = $('pac-crop-img');
      const artR  = getArtRatio();
      const outR  = state.width / state.height;
      const vpW   = viewport.clientWidth;
      const vpH   = viewport.clientHeight;

      let imgW, imgH;
      if (artR > outR) { imgH = vpH; imgW = vpH * artR; }
      else             { imgW = vpW; imgH = vpW / artR; }

      const maxX  = Math.max(1, imgW - vpW);
      const maxY  = Math.max(1, imgH - vpH);

      state.crop.x = Math.max(0, Math.min(1, startCropX - dx / maxX));
      state.crop.y = Math.max(0, Math.min(1, startCropY - dy / maxY));

      updateCropPosition();
      updateMockup();
      e.preventDefault();
    }

    function onUp() { dragging = false; }

    viewport.addEventListener('mousedown',  onDown);
    viewport.addEventListener('touchstart', onDown, { passive: false });
    document.addEventListener('mousemove',  onMove);
    document.addEventListener('touchmove',  onMove, { passive: false });
    document.addEventListener('mouseup',    onUp);
    document.addEventListener('touchend',   onUp);
  }

  // ── Pricing display ──────────────────────────────────────────────────────
  function updatePricingDisplay() {
    const p = state.price;
    const s = state.shipping;
    if (!p) return;

    // Single price line — "Custom Configuration Price" (no base/surcharge split shown)
    $('pac-total-price').textContent = `$${p.totalPrice.toFixed(2)}`;
    $('pac-shipping').textContent    = s ? s.feeLabel : '—';

    // Freight-tier items: relabel the shipping line; the calculated freight charge stays as the value.
    if ($('pac-shipping-label')) {
      $('pac-shipping-label').textContent = (s && s.tier === 4) ? 'This sized work is delivered by FREIGHT' : 'Shipping';
    }

    // Grand total = item price + shipping fee (shipping is folded into the charged price, Option A).
    var grandTotal = p.totalPrice + ((s && s.fee) ? s.fee : 0);
    if ($('pac-grand-total')) $('pac-grand-total').textContent = `$${grandTotal.toFixed(2)}`;

    $('pac-pricing').style.display = 'block';
    $('pac-actions').style.display = 'flex';
  }

  function clearPricing() {
    state.price    = null;
    state.shipping = null;
    if ($('pac-pricing'))      $('pac-pricing').style.display      = 'none';
    if ($('pac-actions'))      $('pac-actions').style.display      = 'none';
    if ($('pac-variant-match'))$('pac-variant-match').style.display = 'none';
  }

  // ── Mockup ───────────────────────────────────────────────────────────────
  // -- Preview (single rectangle -- renders the finished piece, is the crop surface)
  var PAC_FRAME_IN = 1.25; // frame moulding width per side, inches
  var PAC_OAK_V = 'data:image/jpeg;base64,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';
  var PAC_OAK_H = 'data:image/jpeg;base64,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';
  var PAC_FRAME_UID = 0;

  function pacFrameMode(m) {
    if (m && m.indexOf('White')   !== -1) return 'white';
    if (m && m.indexOf('Natural') !== -1) return 'natural';
    return 'black';
  }

  // Mitered SVG frame: four 45-degree-miter rails around a W x H opening.
  // natural = oak-grain image per rail (grain runs along the rail); black/white = beveled solid.
  function pacFrameSVG(mode, OW, OH, F) {
    var uid = 'pacfx' + (++PAC_FRAME_UID);
    function pth(p){ return 'M'+p[0]+' '+p[1]+' L'+p[2]+' '+p[3]+' L'+p[4]+' '+p[5]+' L'+p[6]+' '+p[7]+' Z'; }
    var top=[0,0,OW,0,OW-F,F,F,F], bot=[0,OH,OW,OH,OW-F,OH-F,F,OH-F],
        lef=[0,0,F,F,F,OH-F,0,OH],  rig=[OW,0,OW-F,F,OW-F,OH-F,OW,OH];
    var s = '<svg class="pac-frame-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 '+OW+' '+OH+'" width="'+OW+'" height="'+OH+'" style="position:absolute;inset:0;display:block;pointer-events:none">';
    var fbg = (mode === 'natural') ? '#efd39d' : (mode === 'black' ? '#0d0d0c' : '#eae6dd');
    s += '<rect x="0" y="0" width="'+OW+'" height="'+OH+'" fill="'+fbg+'"/>';
    if (mode === 'natural') {
      s += '<defs>'
         +  '<clipPath id="ct_'+uid+'"><path d="'+pth(top)+'"/></clipPath>'
         +  '<clipPath id="cb_'+uid+'"><path d="'+pth(bot)+'"/></clipPath>'
         +  '<clipPath id="cl_'+uid+'"><path d="'+pth(lef)+'"/></clipPath>'
         +  '<clipPath id="cr_'+uid+'"><path d="'+pth(rig)+'"/></clipPath>'
         + '</defs>'
         + '<image href="'+PAC_OAK_H+'" x="0" y="0" width="'+OW+'" height="'+F+'" preserveAspectRatio="xMidYMid slice" clip-path="url(#ct_'+uid+')"/>'
         + '<image href="'+PAC_OAK_H+'" x="0" y="'+(OH-F)+'" width="'+OW+'" height="'+F+'" preserveAspectRatio="xMidYMid slice" clip-path="url(#cb_'+uid+')"/>'
         + '<image href="'+PAC_OAK_V+'" x="0" y="0" width="'+F+'" height="'+OH+'" preserveAspectRatio="xMidYMid slice" clip-path="url(#cl_'+uid+')"/>'
         + '<image href="'+PAC_OAK_V+'" x="'+(OW-F)+'" y="0" width="'+F+'" height="'+OH+'" preserveAspectRatio="xMidYMid slice" clip-path="url(#cr_'+uid+')"/>'
         + '<path d="'+pth(top)+'" fill="rgba(255,255,255,0.02)"/>'
         + '<path d="'+pth(lef)+'" fill="rgba(255,255,255,0.01)"/>'
         + '<path d="'+pth(bot)+'" fill="rgba(0,0,0,0.04)"/>'
         + '<path d="'+pth(rig)+'" fill="rgba(0,0,0,0.03)"/>';
    } else {
      var sh = (mode === 'black')
        ? { t:'#0f0f0e', l:'#0e0e0d', r:'#0e0c0c', b:'#0c0c0b' }
        : { t:'#ece8df', l:'#eae6dd', r:'#e6e2d9', b:'#e4e0d6' };
      s += '<path d="'+pth(top)+'" fill="'+sh.t+'"/>'
         + '<path d="'+pth(lef)+'" fill="'+sh.l+'"/>'
         + '<path d="'+pth(rig)+'" fill="'+sh.r+'"/>'
         + '<path d="'+pth(bot)+'" fill="'+sh.b+'"/>';
    }
    var mstroke = (mode === 'black') ? 'rgba(255,255,255,0.16)'
              : (mode === 'white') ? 'rgba(0,0,0,0.14)'
              : 'rgba(0,0,0,0.05)';
    s += '<line x1="0" y1="0" x2="'+F+'" y2="'+F+'" stroke="'+mstroke+'" stroke-width="1"/>'
       + '<line x1="'+OW+'" y1="0" x2="'+(OW-F)+'" y2="'+F+'" stroke="'+mstroke+'" stroke-width="1"/>'
       + '<line x1="0" y1="'+OH+'" x2="'+F+'" y2="'+(OH-F)+'" stroke="'+mstroke+'" stroke-width="1"/>'
       + '<line x1="'+OW+'" y1="'+OH+'" x2="'+(OW-F)+'" y2="'+(OH-F)+'" stroke="'+mstroke+'" stroke-width="1"/>'
       + '<rect x="'+F+'" y="'+F+'" width="'+(OW-2*F)+'" height="'+(OH-2*F)+'" fill="none" stroke="rgba(0,0,0,0.40)" stroke-width="1.5"/>'
       + '<rect x="0.5" y="0.5" width="'+(OW-1)+'" height="'+(OH-1)+'" fill="none" stroke="rgba(0,0,0,0.22)" stroke-width="1"/>'
       + '</svg>';
    return s;
  }

  // Confirmation deep-clones the live piece; re-namespace cloned frame-SVG ids so they can't collide.
  function pacUniquifyFrameIds(rootEl) {
    if (!rootEl || !rootEl.querySelectorAll) return;
    var svgs = rootEl.querySelectorAll('svg.pac-frame-svg');
    for (var k = 0; k < svgs.length; k++) {
      var svg = svgs[k];
      var ids = svg.querySelectorAll('[id]');
      for (var i = 0; i < ids.length; i++) {
        var oldId = ids[i].getAttribute('id');
        var newId = oldId + '_c' + (++PAC_FRAME_UID);
        ids[i].setAttribute('id', newId);
        var refs = svg.querySelectorAll('[clip-path="url(#' + oldId + ')"]');
        for (var j = 0; j < refs.length; j++) { refs[j].setAttribute('clip-path', 'url(#' + newId + ')'); }
      }
    }
  }


  function pacIsFramed(m)   { return !!m && m.indexOf('Framed') !== -1; }
  function pacFrameClass(m) {
    if (m && m.indexOf('White')   !== -1) return 'pac-preview-frame--white';
    if (m && m.indexOf('Natural') !== -1) return 'pac-preview-frame--natural';
    return 'pac-preview-frame--black';
  }
  function pacBorderIn(m) {
    // Paper Print & Loose Canvas can carry a 1" white border per side (customer toggle).
    // Price is unchanged either way (a 9x12 is priced as an 11x14 sheet).
    if (m !== 'Paper Print' && m !== 'Loose Canvas') return 0;
    return state.border ? 1 : 0;
  }
  function pacBorderApplies(m) { return m === 'Paper Print' || m === 'Loose Canvas'; }
  function pacMatApplies(m) { return pacIsFramed(m); }
  function pacMatIn() { return pacMatApplies(state.media) ? (state.matSize || 0) : 0; }
  function pacMatFactor() {
    if (!pacMatApplies(state.media)) return 1;
    if (state.matSize === 1) return 0.95;
    if (state.matSize === 0) return 0.90;
    return 1; // 2" mat = base price
  }
  function pacBorderTitleSuffix() {
    if (!pacBorderApplies(state.media)) return '';
    return state.border ? ' · 1" White Border' : ' · No Border';
  }
  function pacMediaLabel(m) {
    return {
      'Paper Print':      'Paper Print',
      'Loose Canvas':     'Loose Canvas',
      'Wrapped Canvas':   'Wrapped Canvas',
      'Framed - Black':   'Framed Print — Black',
      'Framed - White':   'Framed Print — White',
      'Framed - Natural': 'Framed Print — Natural',
    }[m] || m;
  }
  function buildCustomTitle() {
    var t = 'CUSTOM: ' + state.artwork.title + ' ' + state.width + '" x ' + state.height + '" ' + pacMediaLabel(state.media);
    if (pacBorderApplies(state.media)) {
      t += state.border ? ' (with 1" white border)' : ' (without white border)';
    }
    if (pacMatApplies(state.media)) {
      t += (state.matSize === 0) ? ' (no mat)' : ' (' + state.matSize + '" ' + state.matColor.toLowerCase() + ' mat)';
    }
    return t;
  }
  function pacNormSize(s) {
    // lowercases, converts × / X to x, strips anything but digits, x, and dot
    return String(s == null ? '' : s).toLowerCase().replace(/[×x]/g, 'x').replace(/[^0-9x.]/g, '');
  }
  function pacNormType(t) {
    return String(t == null ? '' : t).toLowerCase().replace(/\s+/g, ' ').trim();
  }
  // Returns the matching standard product variant, or null if this is a custom order.
  // Paper/Loose: the standard product IS the bordered sheet, so full-bleed (border off) is always custom.
  function getStandardVariant() {
    if (!state.artwork || !state.media || !state.width || !state.height) return null;
    if (pacBorderApplies(state.media) && !state.border) return null;
    if (pacMatApplies(state.media) && (state.matSize !== 2 || state.matColor !== 'White')) return null; // standard framed product = 2" white mat only
    var typeMap = {
      'Paper Print':      'Paper Print',
      'Loose Canvas':     'Loose Canvas Print',
      'Wrapped Canvas':   'Wrapped Canvas',
      'Framed - Black':   'Framed Print - Black',
      'Framed - White':   'Framed Print - White',
      'Framed - Natural': 'Framed Print - Natural',
    };
    var targetType = pacNormType(typeMap[state.media] || state.media);
    var targetSize = pacNormSize(state.width + 'x' + state.height);
    var pool = (state.artwork.allVariants && state.artwork.allVariants.length)
      ? state.artwork.allVariants
      : (state.artwork.variants || []);
    var found = pool.find(function (v) {
      return pacNormSize(v.size) === targetSize && pacNormType(v.product_type) === targetType;
    });
    return found || null;
  }
  function updateBorderToggleVisibility() {
    var wrap = $('pac-border-toggle');
    if (!wrap) return;
    wrap.style.display = pacBorderApplies(state.media) ? 'flex' : 'none';
    var cb = $('pac-border-checkbox');
    if (cb) cb.checked = state.border;
  }
  // ── Mat controls (framed only; injected into the size stage) ──────────────
  function ensureMatUI() {
    if ($('pac-mat-controls')) return;
    var sizeStage = $('pac-stage-size');
    if (!sizeStage) return;
    var wrap = document.createElement('div');
    wrap.id = 'pac-mat-controls';
    wrap.style.display = 'none';
    wrap.style.marginTop = '2rem';
    wrap.innerHTML =
        '<div class="pac-label">Mat</div>'
      + '<div id="pac-mat-size-pills" class="pac-pills" style="margin-top:8px">'
      +   '<button type="button" class="pac-pill" data-mat="2">2&#8243;</button>'
      +   '<button type="button" class="pac-pill" data-mat="1">1&#8243;</button>'
      +   '<button type="button" class="pac-pill" data-mat="0">No mat</button>'
      + '</div>'
      + '<div id="pac-mat-color-row" style="margin-top:14px">'
      +   '<div class="pac-label">Mat colour</div>'
      +   '<div id="pac-mat-color-pills" class="pac-pills" style="margin-top:8px">'
      +     '<button type="button" class="pac-pill" data-matcolor="White">White</button>'
      +     '<button type="button" class="pac-pill" data-matcolor="Black">Black</button>'
      +   '</div>'
      + '</div>';
    var ovLine = $('pac-overall-line');
    if (ovLine && ovLine.parentNode) ovLine.parentNode.insertBefore(wrap, ovLine);
    else sizeStage.appendChild(wrap);
    wrap.querySelectorAll('#pac-mat-size-pills .pac-pill').forEach(function (b) {
      b.addEventListener('click', function () {
        state.matSize = parseInt(this.dataset.mat, 10);
        updateMatActiveStates();
        updateMatColorRow();
        if (state.width && state.height && state.media) onSizeChanged();
        else if (state.media) updateMockup();
      });
    });
    wrap.querySelectorAll('#pac-mat-color-pills .pac-pill').forEach(function (b) {
      b.addEventListener('click', function () {
        state.matColor = this.dataset.matcolor;
        updateMatActiveStates();
        if (state.width && state.height && state.media) updateMockup();
      });
    });
    updateMatActiveStates();
  }
  function updateMatActiveStates() {
    var sp = $('pac-mat-size-pills');
    if (sp) sp.querySelectorAll('.pac-pill').forEach(function (p) {
      p.classList.toggle('pac-pill--active', parseInt(p.dataset.mat, 10) === state.matSize);
    });
    var cp = $('pac-mat-color-pills');
    if (cp) cp.querySelectorAll('.pac-pill').forEach(function (p) {
      p.classList.toggle('pac-pill--active', p.dataset.matcolor === state.matColor);
    });
  }
  function updateMatColorRow() {
    var row = $('pac-mat-color-row');
    if (row) row.style.display = (pacMatApplies(state.media) && state.matSize > 0) ? 'block' : 'none';
  }
  function updateMatUIVisibility() {
    ensureMatUI();
    var wrap = $('pac-mat-controls');
    if (!wrap) return;
    wrap.style.display = pacMatApplies(state.media) ? 'block' : 'none';
    updateMatActiveStates();
    updateMatColorRow();
  }
  function pacOverall() {
    if (pacIsFramed(state.media)) {
      var mO = pacMatIn();
      return [state.width + 2 * mO + 2 * PAC_FRAME_IN, state.height + 2 * mO + 2 * PAC_FRAME_IN];
    }
    var b = pacBorderIn(state.media);
    return [state.width + 2 * b, state.height + 2 * b]; // paper/loose +1" per side; wrapped unchanged (b=0)
  }
  function pacOverflowAxis() {
    if (!state.artwork) return 'none';
    var out = state.width / state.height;
    var art = getRatioDecimal(state.artwork.ratio);
    if (!art || Math.abs(out - art) < 0.001) return 'none';
    return art > out ? 'x' : 'y'; // artwork wider than frame -> horizontal pan
  }
  function updateCrop() {
    var photo = $('pac-preview-photo');
    if (photo) photo.style.objectPosition = (state.crop.x * 100) + '% ' + (state.crop.y * 100) + '%';
  }
  function attachPreviewDrag(el) {
    var dragging = false, sx, sy, scx, scy;
    el.addEventListener('pointerdown', function (e) {
      dragging = true; sx = e.clientX; sy = e.clientY;
      scx = state.crop.x; scy = state.crop.y;
      el.setPointerCapture(e.pointerId);
    });
    el.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var ax = pacOverflowAxis(), r = el.getBoundingClientRect();
      if (ax === 'y')      state.crop.y = Math.max(0, Math.min(1, scy - (e.clientY - sy) / r.height));
      else if (ax === 'x') state.crop.x = Math.max(0, Math.min(1, scx - (e.clientX - sx) / r.width));
      updateCrop();
    });
    el.addEventListener('pointerup',     function () { dragging = false; });
    el.addEventListener('pointercancel', function () { dragging = false; });
  }

  function updateMockup() {
    if (!state.artwork || !state.width || !state.height || !state.media) return;
    var wrap  = $('pac-mockup-wrap');
    var piece = $('pac-preview-piece');
    var ph    = $('pac-mockup-placeholder');
    if (!wrap || !piece) return;

    state.crop = state.crop || { x: 0.5, y: 0.5 };

    var overall = pacOverall(), ow = overall[0], oh = overall[1];
    var baseW  = wrap.clientWidth || 360;
    var baseH  = baseW * 0.75;            // wrap is 4:3 — derive height deterministically, do not read clientHeight mid-layout
    var innerW = baseW * 0.92;
    var innerH = baseH * 0.92;
    var scale  = Math.min(innerW / ow, innerH / oh);
    var px = function (v) { return Math.max(0, v * scale); };

    var artSrc  = state.artwork.artwork_url || state.artwork.image || '';
    var imgHtml = '<div class="pac-preview-img" id="pac-preview-img" style="width:'
                + px(state.width) + 'px;height:' + px(state.height) + 'px">'
                + '<img id="pac-preview-photo" class="pac-preview-photo" crossorigin="anonymous" '
                + 'draggable="false" alt="" src="' + artSrc + '">'
                + '</div>';
    var html;
    if (pacIsFramed(state.media)) {
      var fF    = px(PAC_FRAME_IN);
      var mPx   = px(pacMatIn());
      var fAW   = px(state.width), fAH = px(state.height);
      var openW = fAW + 2 * mPx, openH = fAH + 2 * mPx;
      var fOW   = openW + 2 * fF, fOH = openH + 2 * fF;
      var matFill = (state.matColor === 'Black') ? '#1c1b19' : '#f3f0ea';
      var artBox  = '<div class="pac-preview-opening" style="position:absolute;left:' + mPx + 'px;top:' + mPx + 'px;width:' + fAW + 'px;height:' + fAH + 'px;box-shadow:0 0 0 1px rgba(0,0,0,.18)">' + imgHtml + '</div>';
      var matLayer = (mPx > 0)
        ? '<div class="pac-preview-mat" style="position:absolute;left:' + fF + 'px;top:' + fF + 'px;width:' + openW + 'px;height:' + openH + 'px;background:' + matFill + '">' + artBox + '</div>'
        : '<div style="position:absolute;left:' + fF + 'px;top:' + fF + 'px;width:' + openW + 'px;height:' + openH + 'px">' + imgHtml + '</div>';
      html = '<div class="pac-preview-framewrap" style="position:relative;display:block;width:' + fOW + 'px;height:' + fOH + 'px">'
           +   pacFrameSVG(pacFrameMode(state.media), fOW, fOH, fF)
           +   matLayer
           + '</div>';
    } else if (pacBorderIn(state.media) > 0) {
      html = '<div class="pac-preview-border" style="padding:' + px(pacBorderIn(state.media)) + 'px">'
           + imgHtml + '</div>'; // paper / loose -- 1" white border around the art
    } else {
      html = imgHtml; // wrapped -- bare
    }

    piece.innerHTML     = html;
    piece.style.display = 'block';
    if (ph) ph.style.display = 'none';

    var imgEl = $('pac-preview-img');
    updateCrop();
    attachPreviewDrag(imgEl);

    var cap = $('pac-preview-cap');
    if (cap) cap.textContent = 'Overall Dimensions ' + (Math.round(ow * 100) / 100) + '\u2033 \u00d7 '
                             + (Math.round(oh * 100) / 100) + '\u2033 \u00b7 drag the artwork to reposition';

    var ovVal = $('pac-overall-value');
    if (ovVal) ovVal.textContent = (Math.round(ow * 100) / 100) + '\u2033 \u00d7 '
                                 + (Math.round(oh * 100) / 100) + '\u2033';
    var ovLine = $('pac-overall-line');
    if (ovLine) ovLine.style.display = 'flex';
  }

  function clearMockup() {
    var piece = $('pac-preview-piece');
    var ph    = $('pac-mockup-placeholder');
    if (piece) { piece.style.display = 'none'; piece.innerHTML = ''; }
    if (ph) ph.style.display = 'block';
    var ovLine = $('pac-overall-line');
    if (ovLine) ovLine.style.display = 'none';
  }

  // ── Variant match ────────────────────────────────────────────────────────
  function checkVariantMatch() {
    const matchEl = $('pac-variant-match');
    if (!matchEl) return;
    const match = getStandardVariant();
    if (match) {
      $('pac-variant-link').href = `${state.artwork.url}?variant=${match.id}`;
      matchEl.style.display       = 'block';
    } else {
      matchEl.style.display = 'none';
    }
  }

  // ── Confirmation panel ───────────────────────────────────────────────────
  function buildConfirmationPanel() {
    if ($('pac-confirmation')) return;
    const panel = document.createElement('div');
    panel.id        = 'pac-confirmation';
    panel.className = 'pac-confirmation';
    panel.style.display = 'none';
    panel.innerHTML = `
      <div class="pac-confirmation__inner">
        <p class="pac-header__eyebrow">REVIEW YOUR ORDER</p>
        <h2 class="pac-confirmation__title" id="pac-confirm-title"></h2>
        <div class="pac-confirmation__mockup"  id="pac-confirm-mockup"></div>
        <div class="pac-confirmation__specs"   id="pac-confirm-specs"></div>
        <div class="pac-confirmation__pricing" id="pac-confirm-pricing"></div>
        <div class="pac-confirmation__actions">
          <button class="pac-btn pac-btn--secondary" id="pac-confirm-view-room">View in a Room</button>
          <button class="pac-btn pac-btn--primary"   id="pac-confirm-add">Confirm &amp; Add to Cart</button>
          <button class="pac-btn pac-btn--secondary" id="pac-confirm-back">Back to Edit</button>
        </div>
      </div>
    `;
    document.getElementById('porter-configurator-root').appendChild(panel);
    $('pac-confirm-back').addEventListener('click', function() {
      panel.style.display = 'none';
      document.querySelector('.pac-layout').style.display = 'grid';
    });
    $('pac-confirm-view-room').addEventListener('click', launchRoomViewer);
    $('pac-confirm-add').addEventListener('click', addToCart);
  }

  function showConfirmation() {
    buildConfirmationPanel();
    const art   = state.artwork;
    const media = state.media;
    const w     = state.width;
    const h     = state.height;
    const p     = state.price;
    const s     = state.shipping;

    const mediaLabel = {
      'Paper Print':      'Paper Print',
      'Loose Canvas':     'Loose Canvas',
      'Wrapped Canvas':   'Wrapped Canvas',
      'Framed - Black':   'Framed Print — Black',
      'Framed - White':   'Framed Print — White',
      'Framed - Natural': 'Framed Print — Natural',
    }[media] || media;

    const orderTitle = buildCustomTitle();
    $('pac-confirm-title').textContent = orderTitle;

    const srcPiece      = $('pac-preview-piece');
    const confirmMockup = $('pac-confirm-mockup');
    confirmMockup.innerHTML = '';
    if (srcPiece && srcPiece.style.display !== 'none') {
      const copy = srcPiece.cloneNode(true);
      copy.removeAttribute('id');
      pacUniquifyFrameIds(copy);
      confirmMockup.appendChild(copy);
    }

    let specsHtml =
      `<div class="pac-spec-row"><span>Artwork</span><span>${art.title}</span></div>` +
      `<div class="pac-spec-row"><span>Media</span><span>${mediaLabel}</span></div>` +
      `<div class="pac-spec-row"><span>Size</span><span>${w}" × ${h}"</span></div>`;
    if (pacBorderApplies(media)) {
      specsHtml += `<div class="pac-spec-row"><span>Border</span><span>${state.border ? '1" white border, all sides' : 'None (full bleed)'}</span></div>`;
    }
    if (pacMatApplies(media)) {
      const ovS = pacOverall();
      specsHtml += `<div class="pac-spec-row"><span>Mat</span><span>${state.matSize === 0 ? 'No mat' : state.matSize + '" ' + state.matColor}</span></div>`;
      specsHtml += `<div class="pac-spec-row"><span>Overall</span><span>${Math.round(ovS[0]*100)/100}" × ${Math.round(ovS[1]*100)/100}"</span></div>`;
    }
    $('pac-confirm-specs').innerHTML = specsHtml;

    $('pac-confirm-pricing').innerHTML = `
      <div class="pac-pricing__row">
        <span class="pac-pricing__label">Custom Configuration Price</span>
        <span class="pac-pricing__value">$${p.totalPrice.toFixed(2)}</span>
      </div>
      <div class="pac-pricing__row">
        <span class="pac-pricing__label">${s && s.tier === 4 ? 'This sized work is delivered by FREIGHT' : 'Shipping'}</span>
        <span class="pac-pricing__value">${s ? s.feeLabel : 'Calculated at checkout'}</span>
      </div>
      <div class="pac-pricing__row pac-pricing__row--total">
        <span class="pac-pricing__label">Total</span>
        <span class="pac-pricing__value">$${(p.totalPrice + ((s && s.fee) ? s.fee : 0)).toFixed(2)}</span>
      </div>
    `;

    document.querySelector('.pac-layout').style.display = 'none';
    $('pac-confirmation').style.display = 'block';
  }

  // ── Room Viewer ──────────────────────────────────────────────────────────
  function launchRoomViewer() {
    if (!state.artwork) return;
    var V = (typeof window !== 'undefined' && window.PAGViewer) ? window.PAGViewer
          : (typeof PAGViewer !== 'undefined' ? PAGViewer : null);
    if (!V) { alert('The room viewer is still loading — please try again in a moment.'); return; }
    const mediaMap = {
      'Paper Print':      'Paper Print',
      'Loose Canvas':     'Loose Canvas Print',
      'Wrapped Canvas':   'Wrapped Canvas',
      'Framed - Black':   'Framed Print - Black',
      'Framed - White':   'Framed Print - White',
      'Framed - Natural': 'Framed Print - Natural',
    };
    V.open({
      productTitle:      state.artwork.title,
      artworkUrl:        state.artwork.artwork_url,
      selectedVariantId: 'custom',
      crop:              state.crop ? { x: state.crop.x, y: state.crop.y } : { x: 0.5, y: 0.5 },
      matSize:           pacMatApplies(state.media) ? state.matSize : 0,
      matColor:          (pacMatApplies(state.media) && state.matSize > 0) ? state.matColor : 'White',
      onAddToCart:       addToCart,
      variants: [{
        id:          'custom',
        option1:     mediaMap[state.media] || state.media,
        option2:     `${state.width}x${state.height}`,
        price:       state.price ? state.price.totalPrice.toFixed(2) : '0.00',
        overallDims: (function(){ var o = pacOverall(); return `${Math.round(o[0]*100)/100}" × ${Math.round(o[1]*100)/100}"`; })(),
      }],
    });
  }

  // ── Add to Cart ──────────────────────────────────────────────────────────
  // ── Cloudinary order/email proof URL ─────────────────────────────────────
  // Public PNG of the configured piece (artwork + focal crop + mat + frame),
  // flat colours matched to the cart proof, for the order admin + order emails.
  var PAC_CLOUDINARY = 'dmh7sn5rl';
  function buildProofUrl(art, media, w, h, crop, matSize, matColor, border) {
    var src = (art && (art.artwork_url || art.image)) || '';
    if (!src || !w || !h || !media) return '';
    var K       = 50; // px per inch
    var framed  = pacIsFramed(media);
    var frameIn = framed ? PAC_FRAME_IN : 0;
    var matIn   = framed ? (matSize || 0) : 0;
    var bordIn  = (pacBorderApplies(media) && border) ? 1 : 0;
    var cx = (crop && typeof crop.x === 'number') ? crop.x : 0.5;
    var cy = (crop && typeof crop.y === 'number') ? crop.y : 0.5;
    var frameHex = (media.indexOf('White')   !== -1) ? 'f0ede8'
                 : (media.indexOf('Natural') !== -1) ? 'c4a060' : '1c1c1a';
    var matHex   = (matColor === 'Black') ? '1c1b19' : 'f3f0ea';
    var artW = Math.round(w * K), artH = Math.round(h * K);
    var Rt   = artW / artH;                                  // print (target) aspect
    var Rs   = (art && getRatioDecimal(art.ratio)) || Rt;    // artwork (source) aspect
    var t = [];
    // 1. Replicate the configurator's object-fit:cover + object-position crop as a
    //    fractional source bounding box, then scale to the print size.
    if (Rs > 0) {
      var fw, fh;
      if (Rt < Rs) { fh = 1; fw = Rt / Rs; }  // source wider than print -> crop the sides
      else         { fw = 1; fh = Rs / Rt; }  // source taller than print -> crop top/bottom
      fw = Math.min(fw, 0.9999); fh = Math.min(fh, 0.9999);
      var bx = Math.max(0, cx * (1 - fw));
      var by = Math.max(0, cy * (1 - fh));
      t.push('c_crop,w_' + fw.toFixed(4) + ',h_' + fh.toFixed(4) + ',x_' + bx.toFixed(4) + ',y_' + by.toFixed(4));
      t.push('c_scale,w_' + artW + ',h_' + artH);
    } else {
      t.push('c_fill,w_' + artW + ',h_' + artH + ',g_auto');
    }
    // 2. Mat + frame as nested borders (flat colours matching the cart proof).
    if (bordIn > 0)          t.push('bo_' + Math.round(bordIn  * K) + 'px_solid_rgb:ffffff'); // paper/loose 1" border
    if (framed && matIn > 0) t.push('bo_' + Math.round(matIn   * K) + 'px_solid_rgb:' + matHex); // mat
    if (framed)              t.push('bo_' + Math.round(frameIn * K) + 'px_solid_rgb:' + frameHex); // frame
    t.push('f_png');
    return 'https://res.cloudinary.com/' + PAC_CLOUDINARY + '/image/fetch/'
         + t.join('/') + '/' + encodeURIComponent(src);
  }

  async function addToCart() {
    const art   = state.artwork;
    const media = state.media;
    const w     = state.width;
    const h     = state.height;
    const p     = state.price;
    const s     = state.shipping;
    if (!art || !media || !w || !h || !p) return;

    const btn = $('pac-confirm-add');
    if (btn) { btn.textContent = 'Adding…'; btn.disabled = true; }

    const fail = (msg) => {
      if (btn) { btn.textContent = 'Confirm & Add to Cart'; btn.disabled = false; }
      if (msg) alert(msg);
    };

    // 1. Standard configuration → add the real standard product, not a custom line.
    if (art.variantsPromise) { try { await art.variantsPromise; } catch (e) {} }
    const std = getStandardVariant();
    if (std) {
      try {
        const res = await fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ items: [{ id: std.id, quantity: 1 }] }),
        });
        if (res.ok) { window.location.href = '/cart'; return; }
        fail('There was a problem adding to cart. Please try again.');
      } catch (e) { fail(); }
      return;
    }

    // 2. Custom configuration → add the placeholder variant.
    //    The Cart Transform reads _config_price / Custom Order to set the line's price and title.
    const title        = buildCustomTitle();
    const shippingNote = s ? `${s.label}${s.fee > 0 ? ' ($' + s.fee.toFixed(2) + ')' : ''}` : '';
    const shipFee      = (s && s.fee) ? s.fee : 0;
    const chargedPrice = p.totalPrice + shipFee; // Option A: shipping folded into the charged line price
    const cropNote     = `Crop: x=${state.crop.x.toFixed(3)}, y=${state.crop.y.toFixed(3)}`;
    const borderNote   = pacBorderApplies(media) ? (state.border ? '1" white border, all sides' : 'None (full bleed)') : '';

    const properties = {
      'Custom Order':     title,
      '_config_price':    `${chargedPrice.toFixed(2)}`,
      'Size':             `${w}" × ${h}"`,
      'Product':          pacMediaLabel(media),
      '_config_id':       `pac_${Date.now()}`,
      '_config_artwork':  art.handle,
      '_config_artwork_url': art.artwork_url || art.image || '',
      '_config_proof_url': buildProofUrl(art, media, w, h, state.crop, state.matSize, state.matColor, state.border),
      '_config_media':    media,
      '_config_width':    `${w}`,
      '_config_height':   `${h}`,
      '_config_border':   pacBorderApplies(media) ? (state.border ? 'yes' : 'no') : 'n/a',
      '_config_crop':     cropNote,
      '_config_shipping': shippingNote,
      '_config_mat_size':  pacMatApplies(media) ? `${state.matSize}` : 'n/a',
      '_config_mat_color': (pacMatApplies(media) && state.matSize > 0) ? state.matColor : 'n/a',
      '_config_overall':   (function(){ var o = pacOverall(); return `${Math.round(o[0]*100)/100}x${Math.round(o[1]*100)/100}`; })(),
    };
    if (borderNote) properties['Border'] = borderNote;
    if (pacMatApplies(media)) properties['Mat'] = (state.matSize === 0) ? 'No mat' : `${state.matSize}" ${state.matColor}`;
    if (shipFee > 0) {
      properties['Artwork Price'] = `$${p.totalPrice.toFixed(2)}`;
      properties[(s && s.tier === 4) ? 'Freight Delivery' : (s && s.tier === 3 ? 'Oversize Fee' : 'Shipping')] = `$${shipFee.toFixed(2)}`;
    }

    try {
      const res = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ items: [{ id: PAC_CUSTOM_VARIANT_ID, quantity: 1, properties }] }),
      });
      if (res.ok) { window.location.href = '/cart'; return; }
      fail('There was a problem adding to cart. Please try again.');
    } catch (e) { fail(); }
  }

  // ── Border toggle ────────────────────────────────────────────────────────
  function initBorderToggle() {
    var cb = $('pac-border-checkbox');
    if (!cb) return;
    cb.addEventListener('change', function () {
      state.border = cb.checked;
      if (state.width && state.height && state.media) onSizeChanged();
    });
  }

  // ── Actions ──────────────────────────────────────────────────────────────
  function initActions() {
    const atcBtn = $('pac-add-to-cart');
    if (atcBtn) {
      atcBtn.addEventListener('click', function() {
        if (!state.artwork || !state.media || !state.width || !state.height || !state.price) return;
        showConfirmation();
      });
    }
    const virBtn = $('pac-view-in-room');
    if (virBtn) {
      virBtn.addEventListener('click', launchRoomViewer);
    }
  }

  // ── URL params ───────────────────────────────────────────────────────────
  async function checkUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const handle = params.get('artwork');
    if (handle) {
      try {
        const catalog = await loadCatalog();
        selectArtwork(handle, catalog);
      } catch(e) {}
    }
  }

  // ── Init ─────────────────────────────────────────────────────────────────
  function init() {
    initSearch();
    initChangeArtwork();
    initMediaPills();
    initSizeInputs();
    initBorderToggle();
    ensureMatUI();
    initActions();
    checkUrlParams();
    loadCatalog().catch(() => {}); // pre-load catalog in background
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.PorterConfigurator       = window.PorterConfigurator || {};
  window.PorterConfigurator.state = state;

})();
